import { PropsWithChildren, useEffect, useMemo } from "react";

import {
    AuthenticatedTemplate,
    UnauthenticatedTemplate,
    useMsal,
} from "@azure/msal-react";
import Button from "devextreme-react/button";
import { InteractionType, PublicClientApplication } from "@azure/msal-browser";

import AuthService from "@/services/auth-service";
import { AuthInteractionType, getMsalConfigADB2C } from "@/config/auth";

import "./NoAuth.scss";
import { useUser } from "@/contexts/user";
import { Navigate } from "react-router";
import { NO_MFA_SIGNIN_USERFLOW } from "@/constants";
import { clearExternalOrganisationId } from "@/utils/utils";
import { createMyAccountApiAsync } from "@/services/api-service";
import { useError } from "@/contexts/error";

type NoAuthProps = {
    title1?: string;
    title2?: string;
    title3?: string;
    message?: string;
    messageSpan?: string;
    hideLogInOut?: boolean;
    noMfa?: boolean;
    desiredDirectory?: string; // 'ad' or 'adb2c'
    redirectIfAuthenticated?: boolean;
};

/**
 * Full page component showing hex pattern, welcome text,
 * a `message` indicating they currently can't access any page, and a logout button.
 * @param props
 * @returns
 */
const NoAuthDeprecated = ({
    desiredDirectory,
    hideLogInOut,
    message,
    messageSpan,
    noMfa,
    title1 = "WELCOME TO",
    title2 = "Illuminate",
    title3,
    redirectIfAuthenticated = true,
    children,
}: PropsWithChildren<NoAuthProps>) => {
    const { instance: msalInstance, accounts } = useMsal();
    const errorContext = useError();
    const isAD = AuthService.adConfig?.id === "ad";
    const loginLabel = isAD ? "Staff log in" : "LOGIN";

    const { user } = useUser();

    const redirectUrl = useMemo(() => {
        if (!user) {
            return null;
        }
        return "/";
    }, [user]);

    useEffect(() => {
        const currentDirectory = localStorage.getItem("msalDirectory");
        const hasAttemptedLogout =
            localStorage.getItem("hasAttemptedLogout") === "true";

        if (
            desiredDirectory &&
            (currentDirectory !== desiredDirectory || !hasAttemptedLogout)
        ) {
            // Update local storage to the desired directory
            localStorage.setItem("msalDirectory", desiredDirectory);
            localStorage.setItem("hasAttemptedLogout", "true"); // Assume logout attempt if changing directory

            const performLogout =
                (currentDirectory === "ad" || currentDirectory === "adb2c") &&
                accounts.length > 0;

            if (performLogout) {
                (async () => {
                    const api = await createMyAccountApiAsync(
                        errorContext,
                        msalInstance,
                    );
                    await api.logOut();

                    await msalInstance.logoutPopup();

                    // After logging out, navigate to refresh the page
                    window.location.reload();
                })();
            } else {
                // If no logout is needed, navigate immediately
                window.location.reload();
            }
        }
    }, [msalInstance, accounts, desiredDirectory, errorContext]);

    const onLoginClick = async () => {
        clearExternalOrganisationId();

        let app = msalInstance;

        if (noMfa) {
            const config = getMsalConfigADB2C(NO_MFA_SIGNIN_USERFLOW);
            app = new PublicClientApplication(config.msal);
            await app.initialize();
        }

        const authRequest = AuthService.createAuthRequest({
            interactionType: AuthInteractionType,
        });

        await app.handleRedirectPromise({ navigateToLoginRequestUrl: true });

        if (AuthInteractionType === InteractionType.Redirect) {
            app.loginRedirect(authRequest);
        } else if (AuthInteractionType === InteractionType.Popup) {
            app.loginPopup(authRequest);
        } else {
            throw Error("Invalid interaction type.");
        }
    };

    return (
        <>
            <img
                className="landing-lattice"
                src="/images/LatticePattern.svg"
                alt="Lattice-Pattern_JetCharge"
            />
            <div className="content-container">
                <div className="line-1"></div>
                <h2>{title1}</h2>
                <div className="line-2"></div>
                <h1>{title2}</h1>
                <p className="sub-title">{title3}</p>
                {message && (
                    <p className="login-info spacer">
                        {message}
                        {messageSpan && <span>{messageSpan}</span>}
                    </p>
                )}
                {children}
                {!hideLogInOut && (
                    <>
                        <UnauthenticatedTemplate>
                            {noMfa && isAD && (
                                <p className="login-info spacer">
                                    Staff login is not available for no MFA
                                    login. <br /> Please use DNSP login or click
                                    the button to redirect to /staff.
                                </p>
                            )}
                            <div>
                                <Button
                                    className="content-login-button spacer"
                                    text={loginLabel}
                                    onClick={() =>
                                        noMfa && isAD
                                            ? window.location.replace("/staff")
                                            : onLoginClick()
                                    }
                                />
                            </div>
                        </UnauthenticatedTemplate>
                        <AuthenticatedTemplate>
                            {redirectIfAuthenticated && redirectUrl ? (
                                <Navigate to={redirectUrl} replace />
                            ) : null}
                        </AuthenticatedTemplate>
                    </>
                )}
            </div>
        </>
    );
};

export default NoAuthDeprecated;
